import { useNostr } from '@nostrify/react';
import { type NLoginType, NUser, useNostrLogin } from '@nostrify/react/login';
import { NRelay1 } from '@nostrify/nostrify';
import { Capacitor } from '@capacitor/core';
import { useCallback, useMemo } from 'react';

import { useAuthor } from './useAuthor.ts';
import { signerWithNudge } from '@/lib/signerWithNudge';

export function useCurrentUser() {
  const { nostr } = useNostr();
  const { logins } = useNostrLogin();

  const loginToUser = useCallback((login: NLoginType): NUser => {
    let user: NUser;
    let isBunkerConnected: (() => boolean) | undefined;

    switch (login.type) {
      case 'nsec':
        user = NUser.fromNsecLogin(login);
        break;
      case 'bunker': {
        user = NUser.fromBunkerLogin(login, nostr);
        // Called at nudge time to check whether any of the bunker's relay
        // WebSockets are OPEN. Relay instances are shared with the main pool
        // so pool.relays will contain them once they have been opened.
        const bunkerRelays = (login as Extract<NLoginType, { type: 'bunker' }>).data.relays;
        isBunkerConnected = () => bunkerRelays.some((url) => {
          const relay = nostr.relay(url);
          return relay instanceof NRelay1 && relay.socket.readyState === WebSocket.OPEN;
        });
        break;
      }
      case 'extension':
        user = NUser.fromExtensionLogin(login);
        break;
      default:
        throw new Error(`Unsupported login type: ${login.type}`);
    }
    const signer = Capacitor.isNativePlatform() ? user.signer : signerWithNudge(user.signer, isBunkerConnected);
    return new NUser(user.method, user.pubkey, signer);
  }, [nostr]);

  const users = useMemo(() => {
    const users: NUser[] = [];

    for (const login of logins) {
      try {
        const user = loginToUser(login);
        users.push(user);
      } catch (error) {
        console.warn('Skipped invalid login', login.id, error);
      }
    }

    return users;
  }, [logins, loginToUser]);

  const user = users[0] as NUser | undefined;
  const author = useAuthor(user?.pubkey);

  return {
    user,
    users,
    ...author.data,
  };
}
